import React, { useState, useMemo } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  ListItemIcon,
  ListItemText,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  ListSubheader
} from '@mui/material';
import {
  Business as OrganizationIcon,
  Group as TeamIcon,
  AccountTree as HierarchyIcon,
  ViewList as GroupedIcon
} from '@mui/icons-material';
import { useOrganization } from '../context/OrganizationContext';

interface HierarchicalItem {
  type: 'organization' | 'team';
  id: string;
  name: string;
  role?: string;
  organizationId?: string; // For teams
  selectable: boolean;
  children?: HierarchicalItem[];
}

type ViewMode = 'hierarchy' | 'grouped';

/**
 * HierarchicalOrgTeamSelector (Workspace Selector)
 *
 * Single dropdown selector that displays organizations and their teams.
 * - Two view modes: hierarchical (teams nested under orgs) or grouped (separate sections)
 * - Shows user's role next to each org/team name
 * - Grays out non-selectable organizations (implicit access only)
 * - Handles navigation when items are selected
 */
export const HierarchicalOrgTeamSelector: React.FC = () => {
  const {
    organizations,
    teams,
    currentOrganizationId,
    currentTeamId,
    setCurrentOrganizationId,
    setCurrentTeamId
  } = useOrganization();

  const [viewMode, setViewMode] = useState<ViewMode>('hierarchy');

  // Build hierarchical data structure
  const hierarchicalData = useMemo(() => {
    const items: HierarchicalItem[] = [];

    // Group teams by organization
    const teamsByOrg = teams.reduce((acc, team) => {
      if (!acc[team.organizationId]) {
        acc[team.organizationId] = [];
      }
      acc[team.organizationId].push(team);
      return acc;
    }, {} as Record<string, typeof teams>);

    // Build hierarchy
    organizations.forEach(org => {
      const orgTeams = teamsByOrg[org.id] || [];

      const orgItem: HierarchicalItem = {
        type: 'organization',
        id: org.id,
        name: org.name,
        role: org.role,
        selectable: !org.isImplicit, // Can't select implicit orgs (team-only access)
        children: orgTeams.map(team => ({
          type: 'team',
          id: team.id,
          name: team.name,
          role: team.role,
          organizationId: org.id,
          selectable: true
        }))
      };

      items.push(orgItem);
    });

    return items;
  }, [organizations, teams]);

  // Determine current selection value
  const currentValue = useMemo(() => {
    if (currentTeamId) {
      return `team:${currentTeamId}`;
    } else if (currentOrganizationId) {
      return `org:${currentOrganizationId}`;
    }
    return '';
  }, [currentOrganizationId, currentTeamId]);

  const handleChange = async (event: SelectChangeEvent<string>) => {
    const value = event.target.value;

    if (value.startsWith('org:')) {
      const orgId = value.substring(4);
      // Clear team first, then set organization
      await setCurrentTeamId(null, false);
      setCurrentOrganizationId(orgId, true);
    } else if (value.startsWith('team:')) {
      const teamId = value.substring(5);

      // Find the team to get its organizationId
      const selectedTeam = teams.find(t => t.id === teamId);

      if (selectedTeam && selectedTeam.organizationId !== currentOrganizationId) {
        // Team is in a different org - need to clear current team first, then switch org, then set new team
        if (currentTeamId) {
          // End current team session first
          await setCurrentTeamId(null, false);
        }
        // Set the new organization without navigation
        setCurrentOrganizationId(selectedTeam.organizationId, false);
        // Small delay to ensure organization context is updated before starting new team session
        setTimeout(() => {
          setCurrentTeamId(teamId, true);
        }, 100);
      } else {
        // Same org, just update team
        setCurrentTeamId(teamId, true);
      }
    }
  };

  const renderMenuItem = (item: HierarchicalItem, isChild: boolean = false) => {
    const value = item.type === 'organization' ? `org:${item.id}` : `team:${item.id}`;
    const isSelected = currentValue === value;
    const isImplicit = item.type === 'organization' && !item.selectable;

    return (
      <MenuItem
        key={value}
        value={value}
        disabled={!item.selectable}
        sx={{
          pl: isChild ? 4 : 2,
          opacity: isImplicit ? 0.7 : 1,
          backgroundColor: isSelected ? 'action.selected' : 'transparent',
          '&:hover': {
            backgroundColor: item.selectable ? 'action.hover' : 'transparent'
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          {item.type === 'organization' ? (
            <OrganizationIcon fontSize="small" />
          ) : (
            <TeamIcon fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center">
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected ? 600 : 400,
                    color: isImplicit ? 'text.disabled' : 'text.primary'
                  }}
                >
                  {item.name}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{ ml: 1, fontStyle: 'italic', color: 'text.secondary' }}
              >
                {isImplicit ? '(no role)' : item.role ? `(${item.role})` : ''}
              </Typography>
            </Box>
          }
        />
      </MenuItem>
    );
  };

  const renderGroupedView = () => {
    const items = [];

    // Organizations section
    items.push(
      <ListSubheader key="orgs-header" disableSticky>My Organizations</ListSubheader>
    );

    organizations.forEach(org => {
      const value = `org:${org.id}`;
      const isSelected = currentValue === value;
      const isImplicit = org.isImplicit;

      items.push(
        <MenuItem
          key={value}
          value={value}
          disabled={isImplicit}
          sx={{
            pl: 2,
            opacity: isImplicit ? 0.7 : 1,
            backgroundColor: isSelected ? 'action.selected' : 'transparent',
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <OrganizationIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                  {org.name}
                </Typography>
                {isImplicit && (
                  <Typography variant="caption" sx={{ ml: 1, fontStyle: 'italic', color: 'text.disabled' }}>
                    (via team)
                  </Typography>
                )}
              </Box>
            }
          />
        </MenuItem>
      );
    });

    // Teams section
    items.push(
      <ListSubheader key="teams-header" disableSticky>My Teams</ListSubheader>
    );

    teams.forEach(team => {
      const value = `team:${team.id}`;
      const isSelected = currentValue === value;

      items.push(
        <MenuItem
          key={value}
          value={value}
          sx={{
            pl: 2,
            backgroundColor: isSelected ? 'action.selected' : 'transparent',
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <TeamIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box display="flex" alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                  {team.name}
                </Typography>
                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                  ({team.organizationName})
                </Typography>
              </Box>
            }
          />
        </MenuItem>
      );
    });

    return items;
  };

  if (hierarchicalData.length === 0) {
    return (
      <FormControl variant="outlined" size="small" fullWidth>
        <Select value="" displayEmpty disabled>
          <MenuItem value="">
            <em>No organizations or teams</em>
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
        <FormControl variant="outlined" size="small" sx={{ flex: 1 }}>
          <Select
            value={currentValue}
            onChange={handleChange}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return <em>Select workspace</em>;
              }
              return (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Change workspace
                </Typography>
              );
            }}
          >
          {viewMode === 'hierarchy' ? (
            hierarchicalData.map(org => [
              renderMenuItem(org, false),
              ...(org.children?.map(team => renderMenuItem(team, true)) || [])
            ])
          ) : (
            renderGroupedView()
          )}
        </Select>
      </FormControl>
      <Tooltip title={viewMode === 'hierarchy' ? 'Switch to grouped view' : 'Switch to hierarchy view'}>
        <IconButton
          color="inherit"
          size="small"
          onClick={() => setViewMode(viewMode === 'hierarchy' ? 'grouped' : 'hierarchy')}
          sx={{
            color: 'rgba(255,255,255,0.85)',
            '& svg': { color: '#fff' },
            '&:hover': { background: 'rgba(255,255,255,0.08)', color: '#fff' },
          }}
        >
          {viewMode === 'hierarchy' ? <GroupedIcon fontSize="small" /> : <HierarchyIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};
